import type React from 'react';
import type { Dimensions } from '../../../../../prop-types/dimension-types';
import { useCallback, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { InfoIcon } from 'ui-components/components/info-icon';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { WeightInput } from 'ui-components/components/weight-input';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import BYOLRestrictionsBanner from '../../../../../components/byol-banner';
import { DangerousGoodsForm } from '../../../../../components/dangerous-goods/dangerous-goods-form';
import { MsdsUpload } from '../../../../../components/dangerous-goods/msds-upload/msds-upload';
import { UnitPosition } from '../../../../../enums/units/unit-position';
import type { UnitPosition as UnitPositionType } from '../../../../../enums/units/unit-display-types';
import { useEnableDangerousGoods } from '../../../../../feature-flags/hooks/use-enable-dangerous-goods';
import { useEnableFulfillmentRates } from '../../../../../feature-flags/hooks/use-enable-fulfillment-rates';
import { useEnableProductItemWeightAndDimensions } from '../../../../../feature-flags/hooks/use-enable-product-item-weight-and-dimensions';
import { useIsAutoSplitEnabledForSeller } from '../../../../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useIsDeliveryOptionsAnz } from '../../../../../pages/settings/stores/selected-store/delivery-options-settings/hooks';
import Global from '../../../../../store/global-selectors';
import { BundleContext } from '../../../bundle-context';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { messages } from '../../../messages';
import style from './shipping-defaults.module.less';
import { AutoSplitRadioButtons } from '../../auto-split-radio-buttons';
import { ProductDetailsDimensionsPresetGroupDisplay } from '../../dimensions-inherited-display';
import { ProductDetailsEnabledCascadingDropdowns } from '../../enabled-cascading-dropdowns';
import { ProductDetailsPresetGroupDropdown } from '../../preset-group-dropdown';
import { ProductDetailsWeightPresetGroupDisplay } from '../../weight-inherited-display';

type MsdsFile = {
  fileName: string;
  uploadDate: string;
};

const ZERO_USD = { value: 0, code: 'USD' };

export const ProductDetailsShippingDefaultsV3 = () => {
  const {
    productId,
    userHasPermission,
    weightValue: productWeightValue,
    setWeightValue,
    weightUnit: productWeightUnit,
    productWarehouseLocation,
    setProductWarehouseLocation,
    productFulfillmentSku,
    setProductFulfillmentSku,
    dimensions,
    setDimensions,
    sizeUnit,
    autoSplitOption,
    setAutoSplitOption,
    autoSplitCustomQuantity,
    setAutoSplitCustomQuantity,
    productPrice,
    setProductPrice,
    enableProductPrice,
    setEnableProductPrice,
    productDangerousGoods,
    setProductDangerousGoods,
    productMsdsFile,
    setProductMsdsFile,
    validationErrors,
    productItemWeight,
    setProductItemWeight,
    productItemDimensions,
    setProductItemDimensions,
  } = useProductDetailsContext();
  const { isBundle, componentsWeight } = useContext(BundleContext);
  const intl = useIntl();

  const fulfillmentRatesEnabled = useEnableFulfillmentRates();
  const isAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const weightValue = isBundle ? componentsWeight?.value : productWeightValue;
  const weightUnit = isBundle ? componentsWeight?.unit : productWeightUnit;
  const isDeliveryOptionsAnz = useIsDeliveryOptionsAnz();
  const isDangerousGoodsOptionEnabled = useEnableDangerousGoods();
  const isProductItemWeightAndDimensionsEnabled = useEnableProductItemWeightAndDimensions();

  const isDangerousGoodsEnabled = !!productDangerousGoods;

  // Auto-split: local state controls panel visibility, independent of the selected option
  const [isAutoSplitPanelOpen, setIsAutoSplitPanelOpen] = useState(!!autoSplitOption && autoSplitOption !== 'DoNotAutoSplit');

  const onAutoSplitToggle = useCallback(
    (checked: boolean) => {
      setIsAutoSplitPanelOpen(checked);
      if (!checked) {
        setAutoSplitOption('DoNotAutoSplit');
      }
    },
    [setAutoSplitOption],
  );

  // Dangerous goods toggle
  const onToggleDangerousGoods = useCallback(
    (value: boolean) => {
      setProductDangerousGoods(
        value
          ? {
              dangerousAmount: {
                amount: 0,
                unit: 'g',
              },
            }
          : null,
      );
    },
    [setProductDangerousGoods],
  );

  // Fulfillment rates — inline CurrencyInput (logic from ProductFee)
  const [currencyInputValue, setCurrencyInputValue] = useState(productPrice ?? ZERO_USD);
  const [isProductFeeOverLimit, setIsProductFeeOverLimit] = useState((productPrice?.value ?? 0) > 9999999.99);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const onFulfillmentRatesToggle = useCallback(
    (checked: boolean) => {
      setEnableProductPrice(checked);
    },
    [setEnableProductPrice],
  );

  const onProductFeeValueChange = useCallback(
    (updatedCurrencyValue: string | number | null | undefined) => {
      const numericValue = updatedCurrencyValue ? Number(updatedCurrencyValue) : 0.0;
      const newValue = { value: numericValue, code: currencyCode };
      setCurrencyInputValue(newValue);
      setIsProductFeeOverLimit(numericValue > 9999999.99);
    },
    [currencyCode],
  );

  const onProductFeeBlur = useCallback(() => {
    const newProductPrice = currencyInputValue.value
      ? { value: Number(currencyInputValue.value), code: currencyCode }
      : { value: 0.0, code: currencyCode };
    setProductPrice(newProductPrice);
  }, [setProductPrice, currencyInputValue, currencyCode]);

  return (
    <div className={style['shipping-tab']}>
      {/* === MAIN CONTENT CARD (Preset + Shipping preferences + Location & dimensions) === */}
      <div className={style['content-card']}>
        {/* Preset */}
        <div className={style['preset-row']}>
          <ProductDetailsPresetGroupDropdown />
        </div>

        {/* BYOL banner (conditional, shown when applicable) */}
        <div className={style['byol-banner']}>
          <BYOLRestrictionsBanner contentText={messages.BYOLRestrictionsBannerText} />
        </div>

        {/* Shipping preferences */}
        <div className={style.section}>
          <Text element='div' textType={TextType.H4} value={messages.shippingPreferencesTitle} />

          <div className={style['shipping-options']}>
            {/* Domestic row: labels then dropdowns */}
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultServiceName} />
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultPackageName} />
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultConfirmation} />
            <ProductDetailsEnabledCascadingDropdowns />
            {/* International row: labels then dropdowns */}
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalServiceName}
            />
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalPackageName}
            />
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalConfirmation}
            />
            <ProductDetailsEnabledCascadingDropdowns isInternational />
          </div>
        </div>
      </div>

      {/* === DIMENSIONS === */}
      <Text className={style['section-title']} element='div' textType={TextType.H4} value={messages.dimensionsSectionTitle} />
      <FlexRow className={style['dimensions-row']} gap={Gap.Space12}>
        {/* Package Dimensions card */}
        <div className={style['dimensions-card']}>
          <div>
            <FlexRow className={style.label} gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.packageWeight} />
              {isBundle ? (
                <InfoIcon
                  color={THEME_CONSTANTS.unstyled}
                  tooltipText={<Text textType={TextType.Caption} value={messages.bundleComponentWeight} />}
                />
              ) : null}
            </FlexRow>
            <div className={style['weight-wrapper']}>
              <WeightInput
                disabled={!userHasPermission || isBundle}
                inputClassName={style['weight-input']}
                onChange={setWeightValue}
                unit={weightUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
                weight={weightValue || 0}
              />
              <ProductDetailsWeightPresetGroupDisplay />
            </div>
          </div>
          <div>
            <FlexRow className={style.label} gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.packageDimensions} />
              {isDeliveryOptionsAnz ? (
                <InfoIcon tooltipText={<Text textType={TextType.Caption} value={messages.shippingDefaultsTooltip} />} />
              ) : null}
            </FlexRow>
            <div className={style['size-wrapper']}>
              <DimensionInput
                dimensions={dimensions as Dimensions}
                disabled={!userHasPermission}
                inputClassName={style['size-input']}
                onChange={setDimensions}
                unit={sizeUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
              />
              <ProductDetailsDimensionsPresetGroupDisplay />
            </div>
          </div>
        </div>

        {/* Item Dimensions card (conditional) */}
        {isProductItemWeightAndDimensionsEnabled ? (
          <div className={style['dimensions-card']}>
            <div>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.packageItemWeight} />
              <div className={style['weight-wrapper']}>
                <WeightInput
                  disabled={!userHasPermission || isBundle}
                  inputClassName={style['weight-input']}
                  onChange={(weight: number, unit: string) =>
                    setProductItemWeight({
                      value: weight,
                      unit: unit || productItemWeight.unit || weightUnit,
                    })
                  }
                  unit={productItemWeight.unit || weightUnit}
                  unitPosition={UnitPosition.InsideStart as UnitPositionType}
                  weight={productItemWeight.value || 0}
                />
              </div>
            </div>
            <div>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.packageItemDimensions} />
              <div className={style['size-wrapper']}>
                <DimensionInput
                  dimensions={productItemDimensions as Dimensions}
                  disabled={!userHasPermission}
                  inputClassName={style['size-input']}
                  onChange={(dims: Dimensions) =>
                    setProductItemDimensions({
                      ...dims,
                      unit: dims.unit ?? productItemDimensions.unit ?? sizeUnit ?? null,
                    })
                  }
                  unit={productItemDimensions.unit || sizeUnit}
                  unitPosition={UnitPosition.InsideStart as UnitPositionType}
                />
              </div>
            </div>
          </div>
        ) : null}
      </FlexRow>

      {/* === LOCATION === */}
      <Text className={style['section-title']} element='div' textType={TextType.H4} value={messages.locationSectionTitle} />
      <div className={style['content-card']}>
        <div className={style.section}>
          <div className={style['location-grid']}>
            <div>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.warehouseLocation} />
              <Input
                className={style['input-wrapper']}
                data-testid='warehouse-location'
                disabled={!userHasPermission}
                inlineMessage={validationErrors?.warehouseLocation?.message}
                inlineMessageType={validationErrors?.warehouseLocation?.message ? INLINE_MESSAGE_TYPES.error : undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setProductWarehouseLocation(event.target.value)}
                value={productWarehouseLocation}
                version={version2}
              />
            </div>
            <div>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.fulfillmentSku} />
              <Input
                className={style['input-wrapper']}
                data-testid='fulfillment-sku'
                disabled={!userHasPermission}
                onChange={setProductFulfillmentSku}
                value={productFulfillmentSku}
              />
            </div>
          </div>
        </div>
      </div>

      {/* === TOGGLE CARDS (each in its own bordered card) === */}
      {isAutoSplitEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-auto-split`}
            checked={isAutoSplitPanelOpen}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.enableAutoSplitLabel)}
            onChange={onAutoSplitToggle}
          />
          {isAutoSplitPanelOpen ? (
            <div className={style['expanded-content']}>
              <AutoSplitRadioButtons
                currentQuantity={autoSplitCustomQuantity}
                disabled={!userHasPermission}
                onChange={setAutoSplitOption}
                selectedSplitOption={autoSplitOption || 'DoNotAutoSplit'}
                setAutoSplitCustomQty={setAutoSplitCustomQuantity}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {fulfillmentRatesEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-fulfillment-rates`}
            checked={enableProductPrice}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.enableFulfillmentRatesLabel)}
            onChange={onFulfillmentRatesToggle}
          />
          <Text className={style.caption} element='div' textType={TextType.Caption} value={messages.enableFulfillmentRatesCaption} />
          {enableProductPrice ? (
            <div className={style['expanded-content']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.productFee} />
              <div className={style['product-fee-input']}>
                <CurrencyInput
                  currencyCode={currencyCode}
                  disabled={!userHasPermission}
                  onBlur={onProductFeeBlur}
                  onChange={onProductFeeValueChange}
                  showPlaceHolderForZero
                  value={currencyInputValue.value}
                />
                {isProductFeeOverLimit ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                    <Text textType={TextType.Caption} value={messages.productFeeOverLimit} />
                  </InlineMessage>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {isDangerousGoodsOptionEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-dangerous-goods`}
            checked={isDangerousGoodsEnabled}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.markDangerousGoodLabel)}
            onChange={onToggleDangerousGoods}
          />
          {isDangerousGoodsEnabled ? (
            <div className={style['expanded-content']}>
              <FlexColumn gap={Gap.Space16}>
                <DangerousGoodsForm onChange={setProductDangerousGoods} value={productDangerousGoods} />
                {Boolean(productId) && (
                  <MsdsUpload
                    currentMsdsFile={productMsdsFile as MsdsFile | null}
                    isDisabled={!userHasPermission}
                    productId={String(productId!)}
                    setCurrentMsdsFile={setProductMsdsFile}
                  />
                )}
              </FlexColumn>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
